import * as React from 'react'
import dynamic from 'next/dynamic'

import { ProductBlock } from '@thg-commerce/enterprise-components'
import { MarketedSpecialOfferSummaryContainerProps } from '@thg-commerce/gravity-patterns/MarketedSpecialOfferSummary/MarketedSpecialOfferSummaryContainer'

import { SwatchShape, SwatchSize } from '@thg-commerce/gravity-elements/Swatch'
import { ProductBlockSkeleton } from '@thg-commerce/gravity-patterns'
import { ProductPriceProps } from '@thg-commerce/gravity-elements'
import { BreakpointArray } from '@thg-commerce/gravity-theme'
import { CustomerContext } from '@thg-commerce/enterprise-core/src/CustomerContext/CustomerContext'
import { useSiteConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/hooks'

import { StyledDeliveryIcon, StyledStoreIcon } from './styles'

export type ProductListMarketedSpecialOfferProps = Omit<
  MarketedSpecialOfferSummaryContainerProps,
  'i18nText' | 'loading'
>

export interface ProductItemProps {
  sku: string
  url: string
  title: string
  inStock: boolean
  brand: {
    name: string
  }
  price?: ProductPriceProps
  images: {
    thumbnail?: string
    largeProduct?: string
    zoom?: string
  }[]
  icons?: {
    deliveryAvailable: boolean
    storeAvailable: boolean
  }
  reviews?: {
    total: number
    averageScore: number
  }
  marketedSpecialOffer?: ProductListMarketedSpecialOfferProps
  colourSwatches?: {
    key: string
    colour: string
    title: string
    href: string
  }[]

  quickBuyFeatureEnabled?: boolean
  productI18nText: {
    buyAriaLabel: (variables: string | string[]) => string
    buyText: string
    quickBuyText: string
    soldOutText: string
    reviewsStarsLabel: string
    reviewsLabel: string
    closeI18nText: {
      closeAriaLabel: string
      closeLabel: string
    }
    freeGiftMessage: string
    swatchUnavailableText: string
    swatchCloseButtonText: string
  }
  wishlistI18nText: {
    addToWishlistText: string
    savedToWishlistText: string
    wishlistTooltip: {
      closeButtonText: string
    }
    wishlistTooltipContent: {
      loginSignupText: string
      toUseWishlistText: string
    }
  }
  siteConfig: {
    hideProductListReviewRating?: boolean
    hideProductListProductBlockButton?: boolean
    productListReviewRatingThreshold?: number
    showMarketedSpecialOfferIcon?: boolean
    hideProductListImageRollover?: boolean
    showProductListProductBlockIcons?: boolean
    hasClickAndCollect?: boolean
    showPdpLinkWhenOutOfStock?: boolean
    enableHorizontalFacets?: boolean
    showProductBlockBrandTitle?: boolean
    hideProductListSwatch?: boolean
  }
  attributesInsetSpacing: BreakpointArray<number>
}

const DEFAULT_MIN_RATING_VALUE = 0

const WishlistButton = dynamic(
  () =>
    import(
      '@thg-commerce/enterprise-components/Product/src/WishlistButton/WishlistButton'
    ),
)

export const ProductItem = (props: ProductItemProps) => {
  const {
    hideProductListReviewRating,
    hideProductListProductBlockButton,
    productListReviewRatingThreshold,
    showMarketedSpecialOfferIcon,
    hideProductListImageRollover,
    showProductListProductBlockIcons,
    hasClickAndCollect,
    showPdpLinkWhenOutOfStock,
    showProductBlockBrandTitle,
    hideProductListSwatch,
  } = props.siteConfig

  const { wishlist } = React.useContext(CustomerContext)

  const inWishlist = wishlist?.skus.includes(parseInt(props.sku, 10))

  const { enableWishlists } = useSiteConfig()

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${props.productI18nText.reviewsStarsLabel}
  ${total} ${props.productI18nText.reviewsLabel}`

  const minRatingValue =
    productListReviewRatingThreshold ?? DEFAULT_MIN_RATING_VALUE

  const productBlockIcons =
    showProductListProductBlockIcons && props.icons ? (
      <React.Fragment>
        <div data-testid="home-delivery-icon">
          <StyledDeliveryIcon available={props.icons.deliveryAvailable} />
        </div>
        {hasClickAndCollect && (
          <div data-testid="available-in-store-icon">
            <StyledStoreIcon available={props.icons.storeAvailable} />
          </div>
        )}
      </React.Fragment>
    ) : undefined

  return (
    <React.Fragment>
      <ProductBlock
        attributesInsetSpacing={props.attributesInsetSpacing}
        price={props.price}
        sku={props.sku}
        title={{ value: props.title }}
        brand={props.brand}
        url={props.url}
        review={
          props.reviews &&
          !hideProductListReviewRating &&
          props.reviews.averageScore >= minRatingValue
            ? {
                starRating: props.reviews.averageScore,
                numberOfReviews: props.reviews.total,
                screenReaderOnlyText: screenReaderReviewText({
                  score: props.reviews.averageScore,
                  total: props.reviews.total,
                }),
              }
            : undefined
        }
        image={{
          isAmp: false,
          urls:
            props.images.length > 0
              ? {
                  largeProduct: props.images[0].largeProduct,
                  thumbnail: props.images[0].thumbnail,
                }
              : {},
        }}
        marketedSpecialOffer={
          props.marketedSpecialOffer && {
            ...props.marketedSpecialOffer,
            loading: false,
            onlyDisplayOfferBadge: true,
            showIcon: showMarketedSpecialOfferIcon,
            i18nText: {
              closeI18nText: props.productI18nText.closeI18nText,
              freeGiftMessage: props.productI18nText.freeGiftMessage,
            },
          }
        }
        hoverImage={
          !hideProductListImageRollover && props.images.length > 1
            ? {
                isAmp: false,
                urls: {
                  largeProduct: props.images[1].largeProduct,
                  thumbnail: props.images[1].thumbnail,
                },
                lazy: true,
              }
            : undefined
        }
        button={
          !hideProductListProductBlockButton
            ? props.inStock
              ? {
                  title: props.productI18nText.buyText,
                  ariaLabel: props.productI18nText.buyAriaLabel(props.title),
                  quickBuyTitle: props.productI18nText.quickBuyText,
                }
              : {
                  title: props.productI18nText.soldOutText,
                  quickBuyTitle: '',
                  disabled: !showPdpLinkWhenOutOfStock,
                }
            : undefined
        }
        icons={productBlockIcons}
        swatch={
          props.colourSwatches
            ? {
                shape: SwatchShape.Circle,
                size: SwatchSize.Small,
                colours: props.colourSwatches,
                i18nText: {
                  unavailableText: props.productI18nText.swatchUnavailableText,
                  closeButtonText: props.productI18nText.swatchCloseButtonText,
                },
              }
            : undefined
        }
        attributes={{
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
        quickBuyFeatureEnabled={props?.quickBuyFeatureEnabled}
        showBrandTitle={showProductBlockBrandTitle}
        hideProductListSwatch={hideProductListSwatch}
      />
      {enableWishlists && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '24px',
          }}
        >
          <WishlistButton
            heartButton={true}
            sku={parseInt(props.sku, 10)}
            inWishlist={inWishlist}
            i18nText={props.wishlistI18nText}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export const ProductItemSkeleton = (count: number) => {
  const SkeletonBlocks = [...Array(count)].map((_, index) => (
    <div key={index} data-testid={`product-list-item-skeleton-${index}`}>
      <ProductBlockSkeleton />
    </div>
  ))
  return SkeletonBlocks
}

export default ProductItem
